import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtMoney, fmtDate, fmtDateTime, formatMarketValue } from "../lib/format";
import {
  AlertTriangle, Inbox, FileSignature, ChevronRight, Calendar, Plus,
  ShieldAlert, Phone, Activity, QrCode, RotateCw, ExternalLink,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import AssistantWidget from "../components/assistant/AssistantWidget";
import { OnboardingChecklist } from "../components/OnboardingGate";

const TIER_TONE = { red: "border-red-300 bg-red-50/60", amber: "border-amber-300 bg-amber-50/60", green: "border-emerald-300 bg-emerald-50/60" };
const TYPE_ICON = { contract_expiry: FileSignature, case: AlertTriangle, inbox: Inbox };

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [overdueCount, setOverdueCount] = useState(0);

  useEffect(() => {
    api.get("/dashboard/v2").then((r) => setData(r.data));
    api.get("/travel-planner/clients-overdue").then((r) => setOverdueCount((r.data || []).length)).catch(() => {});
  }, [user?.role]);

  if (!data) return <Layout title="Loading dashboard…" />;

  const { urgent, roster, todays_events, upcoming_events, receivables, licence_alert, licence_card } = data;
  const eventsList = todays_events.length ? todays_events : upcoming_events;

  return (
    <Layout title="Operations Dashboard" subtitle="Today at a glance">
      {/* FIFA licence credit card + receivables top strip */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-4 mb-6">
        <LicenceCreditCard card={licence_card} alert={licence_alert} />
        <StatCard label="Outstanding commission" value={fmtMoney(receivables.total_outstanding)} accent="emerald" to="/commissions" testid="dash-outstanding" />
        <StatCard label="Active clients" value={roster.length} to="/clients" testid="dash-clients" />
        <Link
          to="/travel-planner#overdue"
          className={`pm-card p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg border ${overdueCount > 0 ? "border-amber-300 bg-amber-50/40" : "border-emerald-200 bg-emerald-50/30"}`}
          data-testid="dash-overdue-card"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1.5">
            <Activity size={11} className={overdueCount > 0 ? "text-amber-600" : "text-emerald-600"} /> Not visited 45+ days
          </div>
          <div className={`font-display text-3xl font-black tracking-tight mt-1 ${overdueCount > 0 ? "text-amber-700" : "text-emerald-700"}`}>{overdueCount}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{overdueCount > 0 ? "Plan a visit →" : "Smaller-client guarantee on track"}</div>
        </Link>
      </div>

      {(user?.role === "agent" || user?.role === "admin") && <><OnboardingChecklist /><AssistantWidget /></>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr_1fr] gap-5">
        {/* LEFT — urgent feed */}
        <div className="pm-card p-5" data-testid="urgent-feed">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Action required</div>
              <h2 className="font-display text-xl font-bold tracking-tight">Urgent feed</h2>
            </div>
            <span className="pm-pill pm-pill-red">{urgent.length}</span>
          </div>
          <div className="space-y-2 pm-scroll overflow-y-auto" style={{ maxHeight: 640 }}>
            {urgent.length === 0 && <div className="text-sm text-slate-500">Inbox zero. Nothing on fire.</div>}
            {urgent.map((u, i) => {
              const Icon = TYPE_ICON[u.type] || AlertTriangle;
              // Deep-link routing — every urgent item must open the EXACT
              // entity it refers to, never a parent list. Each receiving
              // page reads location.state (or the query string) and
              // auto-opens that detail panel/drawer on mount.
              let path = `/clients/${u.client_id}`;
              let linkState = null;
              if (u.type === "inbox") {
                path = "/inbox";
                linkState = u.id ? { openThreadId: u.id } : null;
              } else if (u.type === "case") {
                path = `/cases/${u.id}`;
              } else if (u.type === "contract_expiry") {
                path = `/contracts?open=${u.id}`;
              } else if (u.type === "mandate") {
                path = "/mandates";
                linkState = { openMandateId: u.id };
              } else if (u.type === "licence" || u.type === "licence_expiry") {
                path = "/legal?tab=licence";
              } else if (u.type === "commission" || u.type === "commission_overdue") {
                // Outstanding commission entries deep-link to the Commissions
                // page (NOT the client profile) with the matching row
                // scrolled into view and ring-highlighted.
                path = `/commissions?open=${u.id}`;
              } else if (u.type === "unvisited" && u.client_id) {
                path = `/clients/${u.client_id}`;
              }
              return (
                <Link
                  key={i}
                  to={path}
                  state={linkState || undefined}
                  data-testid={`urgent-${u.type}-${u.id}`}
                  className={`block rounded-xl border p-3 transition-all hover:translate-y-[-1px] ${TIER_TONE[u.tier] || "border-slate-200 bg-white"}`}
                >
                  <div className="flex items-start gap-2">
                    <Icon size={14} className="text-slate-700 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900 truncate">{u.label}</div>
                      <div className="text-[11px] text-slate-600 truncate">{u.detail}</div>
                    </div>
                    <span className="font-mono-tab text-[10px] text-slate-500">{u.score}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* CENTER — client roster */}
        <div className="pm-card p-5" data-testid="dash-roster">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Roster</div>
              <h2 className="font-display text-xl font-bold tracking-tight">My clients</h2>
            </div>
            <Link to="/clients/new" className="pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1"
                  data-testid="dash-add-client">
              <Plus size={12} /> Add
            </Link>
          </div>
          <div className="space-y-1 pm-scroll overflow-y-auto" style={{ maxHeight: 640 }}>
            {roster.map((c) => {
              const d = c.days_to_contract_end;
              const tone = d == null ? "text-slate-400" : d <= 180 ? "text-red-600" : d <= 365 ? "text-amber-600" : "text-emerald-700";
              const dot = d == null ? "bg-slate-300" : d <= 180 ? "bg-red-500" : d <= 365 ? "bg-amber-500" : "bg-emerald-500";
              const stale = c.days_since_contact != null && c.days_since_contact > 30;
              const contactDot =
                c.days_since_contact == null ? "bg-slate-200" :
                c.days_since_contact <= 14 ? "bg-emerald-500" :
                c.days_since_contact <= 30 ? "bg-amber-500" : "bg-red-500";
              const contactTitle = c.days_since_contact == null ? "No contact logged" : `${c.days_since_contact}d since last contact`;
              const v = c.days_since_visit;
              const visitDot = v == null ? "bg-slate-200" : v <= 30 ? "bg-emerald-500" : v <= 60 ? "bg-amber-500" : "bg-red-500";
              const visitTitle = v == null ? "No match visit logged" : `${v}d since last match visit`;
              const mv = c.market_value_eur;
              return (
                <Link key={c.id} to={`/clients/${c.id}`} data-testid={`roster-${c.id}`}
                      className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors group">
                  <div className="flex flex-col gap-1 items-center">
                    <span className={`w-2 h-2 rounded-full ${dot}`} title={d != null ? `${d}d to contract end` : "—"} data-testid={`roster-${c.id}-contract-dot`} />
                    <span className={`w-2 h-2 rounded-full ${contactDot}`} title={contactTitle} data-testid={`roster-${c.id}-health-dot`} />
                    <span className={`w-2 h-2 rounded-full ${visitDot}`} title={visitTitle} data-testid={`roster-${c.id}-visit-dot`} />
                  </div>
                  <img src={c.photo_url} alt="" className="w-9 h-9 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-slate-900 truncate group-hover:text-emerald-700">{c.full_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.position} · {c.current_club}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-tab text-xs font-bold text-slate-800">{formatMarketValue(mv)}</div>
                    <div className={`text-[10px] font-mono-tab ${tone}`}>{d != null ? `${d}d` : "—"}</div>
                  </div>
                  {stale && <span title={`${c.days_since_contact}d since contact`} className="text-red-500"><ShieldAlert size={12} /></span>}
                </Link>
              );
            })}
          </div>
        </div>

        {/* RIGHT — today + quick add */}
        <div className="space-y-5">
          <div className="pm-card p-5" data-testid="dash-today">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{todays_events.length ? "Today" : "Next 7 days"}</div>
                <h2 className="font-display text-xl font-bold tracking-tight">Calendar</h2>
              </div>
              <Link to="/schedule" className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1">View all <ChevronRight size={11} /></Link>
            </div>
            <div className="space-y-2 pm-scroll overflow-y-auto" style={{ maxHeight: 280 }}>
              {eventsList.length === 0 && <div className="text-sm text-slate-500">Nothing scheduled.</div>}
              {eventsList.map((e, idx) => (
                <Link key={e.id} to={`/schedule?open=${e.id}`} className="block px-2 py-2 rounded-lg hover:bg-slate-50" data-testid={`dash-today-event-${idx}`}>
                  <div className="text-sm font-semibold text-slate-900 truncate">{e.title}</div>
                  <div className="text-[11px] text-slate-500 font-mono-tab">{fmtDateTime(e.start)}</div>
                </Link>
              ))}
            </div>
          </div>

          <div className="pm-card p-5" data-testid="dash-quick-actions">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Quick add</div>
            <div className="grid grid-cols-2 gap-2">
              <Link to="/clients/new" className="pm-btn-secondary rounded-lg h-10 flex items-center justify-center gap-1 text-xs font-bold" data-testid="qadd-client">
                <Plus size={12} /> Client
              </Link>
              <Link to="/cases" className="pm-btn-secondary rounded-lg h-10 flex items-center justify-center gap-1 text-xs font-bold" data-testid="qadd-case">
                <Plus size={12} /> Case
              </Link>
              <Link to="/inbox" className="pm-btn-secondary rounded-lg h-10 flex items-center justify-center gap-1 text-xs font-bold" data-testid="qadd-msg">
                <Phone size={12} /> Log call
              </Link>
              <Link to="/legal" className="pm-btn-secondary rounded-lg h-10 flex items-center justify-center gap-1 text-xs font-bold" data-testid="qadd-legal">
                <Plus size={12} /> Legal
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function StatCard({ label, value, accent, to, testid }) {
  const tone = accent === "emerald" ? "text-emerald-700" : "text-slate-900";
  return (
    <Link to={to || "#"} className="pm-card pm-card-hover p-5 block" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
      <div className={`font-display font-black text-3xl font-mono-tab mt-2 ${tone}`}>{value}</div>
    </Link>
  );
}

function LicenceCreditCard({ card, alert }) {
  // Credit-card-styled visualisation of the FIFA Intermediary Licence.
  // Front  = card details · Back = QR code that streams the PDF when scanned.
  // Tap flips. We mint the share token lazily on first flip so we don't
  // waste a JWT for every dashboard render.
  const [flipped, setFlipped] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [tokenErr, setTokenErr] = useState("");

  useEffect(() => {
    if (!flipped || shareToken || tokenErr) return;
    api.get("/legal/licence/share-token?ttl_hours=24")
      .then((r) => setShareToken(r.data))
      .catch((e) => setTokenErr(e?.response?.data?.detail || "Could not mint share link"));
  }, [flipped, shareToken, tokenErr]);

  if (!card) {
    return (
      <Link
        to="/legal?tab=licence"
        className="block rounded-2xl p-5 relative overflow-hidden text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
        style={{ background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)", aspectRatio: "1.7/1" }}
        data-testid="licence-card"
      >
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">FIFA Intermediary Licence</div>
        <div className="mt-6 font-display text-lg font-bold">Not on file</div>
        <div className="text-xs opacity-70 mt-1">Upload your licence to enable compliance tracking →</div>
      </Link>
    );
  }
  const tier = alert ? alert.tier : card.tier || "green";
  const bg = tier === "red"
    ? "linear-gradient(135deg, #7f1d1d 0%, #b91c1c 60%, #ef4444 100%)"
    : tier === "amber"
      ? "linear-gradient(135deg, #78350f 0%, #b45309 60%, #f59e0b 100%)"
      : "linear-gradient(135deg, #0f172a 0%, #1e3a8a 55%, #1e40af 100%)";
  const statusText = alert?.expired
    ? `EXPIRED · ${Math.abs(alert.days_to_expiry)}d ago`
    : tier === "red"
      ? `Renew now · ${card.days_to_expiry}d left`
      : tier === "amber"
        ? `Renew soon · ${card.days_to_expiry}d left`
        : "In good standing";
  const lastFour = (card.licence_number || "").slice(-4).padStart(4, "·");
  const shareUrl = shareToken
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/licence/${shareToken.token}`
    : "";

  return (
    <div
      className="relative cursor-pointer"
      style={{ perspective: "1200px", aspectRatio: "1.7/1" }}
      onClick={(e) => {
        // Don't flip if the agent clicked an explicit child link/button
        // (e.g. the "Open in new tab" affordance on the back face).
        if (e.target.closest("a,button")) return;
        setFlipped((f) => !f);
      }}
      data-testid="licence-card"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setFlipped((f) => !f);
        }
      }}
    >
      <div
        className="relative w-full h-full transition-transform duration-700"
        style={{
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0)",
        }}
        data-testid="licence-card-inner"
      >
        {/* FRONT face */}
        <div
          className="absolute inset-0 rounded-2xl p-5 overflow-hidden text-white shadow-lg"
          style={{ background: bg, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
          data-testid="licence-card-front"
        >
          <div className="absolute -right-12 -top-12 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -left-10 bottom-0 w-28 h-28 rounded-full bg-white/5 blur-xl" />
          <div className="flex items-start justify-between relative">
            <div>
              <div className="text-[9px] uppercase tracking-[0.28em] font-bold opacity-80">FIFA Intermediary</div>
              <div className="font-display font-black text-lg leading-tight">Agent Licence</div>
            </div>
            <div className="flex items-center gap-2">
              {tier !== "green" && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/20" data-testid="licence-card-status-pill">
                  {tier === "red" ? "URGENT" : "RENEW"}
                </span>
              )}
              <div className="w-9 h-7 rounded-md bg-gradient-to-br from-yellow-200 to-yellow-400 shadow-inner relative">
                <div className="absolute inset-1 rounded-sm border border-yellow-700/30" />
                <div className="absolute left-1.5 right-1.5 top-1/2 h-px bg-yellow-700/30" />
              </div>
            </div>
          </div>
          <div className="mt-5 font-mono-tab text-base tracking-[0.32em] font-bold relative" data-testid="licence-card-number">
            ····  ····  ····  {lastFour}
          </div>
          <div className="mt-3 flex items-end justify-between relative">
            <div className="min-w-0">
              <div className="text-[8px] uppercase tracking-[0.22em] opacity-70 font-bold">Licence holder</div>
              <div className="text-xs font-bold truncate" data-testid="licence-card-holder">{card.holder_name}</div>
              {card.issuing_body && (
                <div className="text-[10px] opacity-70 mt-0.5">Issued by {card.issuing_body}</div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="text-[8px] uppercase tracking-[0.22em] opacity-70 font-bold">Valid through</div>
              <div className="font-mono-tab text-sm font-bold" data-testid="licence-card-expiry">
                {card.expiry_date ? new Date(card.expiry_date).toLocaleDateString("en-GB", { month: "2-digit", year: "2-digit" }).replace("/", " / ") : "—"}
              </div>
            </div>
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-wider opacity-90" data-testid="licence-card-status">
            {statusText}
          </div>
          {/* Flip-hint pill */}
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          </div>
          <div className="absolute bottom-3 right-3 text-[9px] uppercase tracking-wider opacity-60 font-bold flex items-center gap-1">
            <QrCode size={9} /> Tap for QR
          </div>
        </div>

        {/* BACK face */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden shadow-lg flex items-center justify-stretch"
          style={{
            background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
            transform: "rotateY(180deg)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
          data-testid="licence-card-back"
        >
          {/* Magnetic stripe */}
          <div className="absolute left-0 right-0 top-5 h-9 bg-slate-900" />
          <div className="flex w-full h-full items-center px-4 pt-12 pb-3 gap-4">
            {/* QR code panel */}
            <div className="shrink-0 bg-white p-2 rounded-lg shadow-inner border border-slate-200" data-testid="licence-qr-wrap">
              {shareToken ? (
                <QRCodeSVG
                  value={shareUrl}
                  size={108}
                  level="M"
                  bgColor="#FFFFFF"
                  fgColor="#0f172a"
                  data-testid="licence-qr-svg"
                />
              ) : tokenErr ? (
                <div className="w-[108px] h-[108px] flex items-center justify-center text-[9px] text-red-700 text-center p-2">{tokenErr}</div>
              ) : (
                <div className="w-[108px] h-[108px] flex items-center justify-center text-[10px] text-slate-400">Generating…</div>
              )}
            </div>
            {/* Right-side metadata */}
            <div className="flex-1 min-w-0 text-slate-900">
              <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-slate-500">Scan to verify</div>
              <div className="font-display font-bold text-sm leading-tight mt-0.5">
                FIFA Agent Licence
              </div>
              <div className="text-[10px] text-slate-600 mt-1 leading-snug">
                Hold a phone camera over the QR to open the licence PDF.
                Link expires{shareToken ? ` in ${Math.round((shareToken.expires_in_seconds || 0) / 3600)}h` : ""}.
              </div>
              <div className="mt-2 flex items-center gap-2">
                {shareToken && (
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[10px] font-bold text-blue-700 hover:text-blue-900 flex items-center gap-0.5"
                    data-testid="licence-qr-open-link"
                  >
                    <ExternalLink size={10} /> Open
                  </a>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShareToken(null);
                    setTokenErr("");
                  }}
                  className="text-[10px] font-bold text-slate-600 hover:text-slate-900 flex items-center gap-0.5"
                  data-testid="licence-qr-regen-btn"
                  title="Regenerate link"
                >
                  <RotateCw size={10} /> Regen
                </button>
                <span className="ml-auto text-[9px] uppercase tracking-wider text-slate-500 font-bold">Tap to flip</span>
              </div>
              {card.licence_number && (
                <div className="mt-2 font-mono-tab text-[10px] text-slate-400 truncate">#{card.licence_number}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
